/**
 * 认证工具函数
 * 用于管理用户登录状态、token和用户信息
 */

const TOKEN_KEY = 'auth_token';
const USER_INFO_KEY = 'auth_user_info';
const WELCOME_CONFIGS_KEY = 'auth_welcome_configs';

/**
 * 保存登录信息
 * @param {string} token - JWT令牌
 * @param {Object} loginData - 登录返回的完整数据，包含 token, jti, exp, user
 * @param {Array} welcomeConfigs - 欢迎配置列表（可选，已废弃，从 user.stores 获取）
 */
export function saveAuthInfo(token, loginData = null, welcomeConfigs = []) {
  localStorage.setItem(TOKEN_KEY, token);
  
  // 支持新的登录数据结构：{ token, jti, exp, user: {...} }
  if (loginData && typeof loginData === 'object') {
    // 如果传入的是完整的登录数据对象
    if (loginData.token || loginData.jti || loginData.user) {
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(loginData));
    } else {
      // 兼容旧格式：直接传入用户信息对象
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(loginData));
    }
  } else if (loginData) {
    // 兼容旧格式：第二个参数是用户信息对象
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(loginData));
  }
  
  // 保留欢迎配置的兼容性（已废弃，现在从 user.stores 获取）
  if (welcomeConfigs && welcomeConfigs.length > 0) {
    localStorage.setItem(WELCOME_CONFIGS_KEY, JSON.stringify(welcomeConfigs));
  }
}

/**
 * 获取token
 * @returns {string|null}
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 获取用户信息
 * @returns {Object|null}
 */
export function getUserInfo() {
  const userInfoStr = localStorage.getItem(USER_INFO_KEY);
  if (!userInfoStr) return null;
  
  try {
    return JSON.parse(userInfoStr);
  } catch (e) {
    console.error('解析用户信息失败:', e);
    return null;
  }
}

/**
 * 获取欢迎配置列表
 * @returns {Array}
 */
export function getWelcomeConfigs() {
  const configsStr = localStorage.getItem(WELCOME_CONFIGS_KEY);
  if (!configsStr) return [];
  
  try {
    return JSON.parse(configsStr);
  } catch (e) {
    console.error('解析欢迎配置失败:', e);
    return [];
  }
}

/**
 * 清除登录信息
 */
export function clearAuthInfo() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_INFO_KEY);
  localStorage.removeItem(WELCOME_CONFIGS_KEY);
}

/**
 * 检查是否已登录
 * @returns {boolean}
 */
export function isAuthenticated() {
  const token = getToken();
  const authenticated = !!token;
  console.log('[登录状态检查]', {
    hasToken: !!token,
    tokenLength: token ? token.length : 0,
    authenticated
  });
  return authenticated;
}

/**
 * 获取用户关联的门店ID列表
 * @returns {Array<string|number>}
 */
export function getUserStoreIds() {
  const userInfo = getUserInfo();
  
  // 优先从新的用户信息结构中的 user.stores 获取
  if (userInfo && userInfo.user && Array.isArray(userInfo.user.stores)) {
    return userInfo.user.stores.map(store => store.id || store.store_id).filter(Boolean);
  }
  
  // 兼容旧格式：从欢迎配置中获取
  const welcomeConfigs = getWelcomeConfigs();
  if (welcomeConfigs && welcomeConfigs.length > 0) {
    return welcomeConfigs.map(config => config.storeId || config.store_id).filter(Boolean);
  }
  
  return [];
}

/**
 * 根据门店ID获取欢迎配置
 * @param {string} storeId - 门店ID
 * @returns {Object|null}
 */
export function getWelcomeConfigByStoreId(storeId) {
  // 优先从新的用户信息结构中的 user.stores 获取
  const userInfo = getUserInfo();
  if (userInfo && userInfo.user && Array.isArray(userInfo.user.stores)) {
    const store = userInfo.user.stores.find(s => 
      (s.id && String(s.id) === String(storeId)) || 
      (s.store_id && String(s.store_id) === String(storeId))
    );
    if (store) {
      return store;
    }
  }
  
  // 兼容旧格式：从欢迎配置中获取
  const welcomeConfigs = getWelcomeConfigs();
  return welcomeConfigs.find(config => 
    (config.storeId && String(config.storeId) === String(storeId)) ||
    (config.store_id && String(config.store_id) === String(storeId))
  ) || null;
}

/**
 * 移除指定门店的欢迎配置缓存
 * @param {string} storeId - 门店ID
 */
export function removeWelcomeConfigByStoreId(storeId) {
  // 兼容旧格式：从欢迎配置中移除
  const welcomeConfigs = getWelcomeConfigs();
  const filteredConfigs = welcomeConfigs.filter(config => 
    (config.storeId && String(config.storeId) !== String(storeId)) &&
    (config.store_id && String(config.store_id) !== String(storeId))
  );
  localStorage.setItem(WELCOME_CONFIGS_KEY, JSON.stringify(filteredConfigs));
}

